import { IVitalDataSetting, TSeverityCategory } from '../../types/life-id';

type TChartSectors = Record<TSeverityCategory, { min: number; max: number }>;

export function scaleValue(value: number, r1: [number, number], r2: [number, number]) {
  return ((value - r1[0]) * (r2[1] - r2[0])) / (r1[1] - r1[0]) + r2[0];
}

const CHART_CATEGORIES_RANGE: TChartSectors = {
  high: { min: 250, max: 270 },
  raised: { min: 240, max: 250 },
  normal: { min: 130, max: 240 },
  lowered: { min: 120, max: 130 },
  low: { min: 0, max: 120 },
};

export const CHART_CATEGORIES_LINE_RANGE: TChartSectors = {
  high: {
    min: 94,
    max: 100,
  },
  raised: {
    min: 80,
    max: 94,
  },
  normal: {
    min: 20,
    max: 80,
  },
  lowered: {
    min: 6,
    max: 20,
  },
  low: {
    min: 0,
    max: 6,
  },
};

const CHART_CATEGORIES_RANGE_SINGLE: TChartSectors = {
  high: { min: 0, max: 0 },
  raised: { min: 0, max: 0 },
  normal: { min: 200, max: 270 },
  lowered: { min: 150, max: 180 },
  low: { min: 0, max: 150 },
};

export const CHART_CATEGORIES_LINE_RANGE_SINGLE = {
  high: {
    min: 0,
    max: 0,
  },
  raised: {
    min: 0,
    max: 0,
  },
  normal: {
    min: 20,
    max: 100,
  },
  lowered: {
    min: 6,
    max: 20,
  },
  low: {
    min: 0,
    max: 6,
  },
};

export default function getGraphData(
  settings: IVitalDataSetting[],
  values: (string | number)[],
  oneSided: boolean = false,
  type: 'line' | 'polar' = 'polar',
) {
  const result = {
    degrees: [] as any,
    graphStates: [] as any,
  };

  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    result.degrees.push(getVitalChartDegree(settings[i], Number(value), oneSided, type === 'line'));
    result.graphStates.push(getGraphState(settings[i], Number(value)).state);
  }

  return result;
}

/**
 * Get graph state name
 * @param vitalSettings vital data setting
 * @param value value to compare
 */
function getGraphState(vitalSettings: IVitalDataSetting, value: number) {
  return (
    vitalSettings.graphIndicatorStates.find((s) => value >= s.min && value <= s.max) || {
      state: 'unknown',
      min: 0,
      max: 0,
    }
  );
}

/**
 * Calculate rotation degree for vital data widget
 * @param value value of record
 * @param oneSidedRange if we have only one negative Slider
 * @param vitalSettings from api
 */
const getVitalChartDegree = (
  vitalSettings: IVitalDataSetting,
  value: number | string,
  oneSidedRange?: boolean,
  line?: boolean,
) => {
  const normalizedValue: number = Number(value);
  /**
   * Get ranges
   */
  const range = getGraphState(vitalSettings, +normalizedValue);
  // high
  if (range.state === 'unknown') {
    return 0;
  }

  let chartRange: { min: number; max: number };

  if (line) {
    chartRange = (oneSidedRange ? CHART_CATEGORIES_LINE_RANGE_SINGLE : CHART_CATEGORIES_LINE_RANGE)[
      range.state
    ];
  } else {
    chartRange = (oneSidedRange ? CHART_CATEGORIES_RANGE_SINGLE : CHART_CATEGORIES_RANGE)[
      range.state
    ];
  }

  /**
   * Calculate range size, one part size and value mapped to range
   */
  const sectionRange = Math.abs(chartRange.min - chartRange.max);
  const sectionParts = Math.abs(range.min - range.max);
  const onePart = sectionRange / sectionParts;
  const rangedValue = Math.max(normalizedValue - range.min, 0);

  /**
   * Return degree for rotation of chart nip
   */
  return chartRange.min + rangedValue * onePart;
};
